import {
  ShareCheckboxType,
  ShareInfoCheckbox,
  getShareEmailText,
  getSharePopoverText,
} from "@components/donate/DonateV3/PaymentProcess/components/ShareInfoCheckbox";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import { DonateFormType } from "@components/donate/DonateV3/types";
import {
  COLLECT_ADDRESS,
  useCollectAddress,
  useFundraiserCreatorName,
} from "@components/donate/hooks";
import { css } from "@emotion/react";
import React, { useEffect } from "react";
import { Controller, UseFormReturn } from "react-hook-form";

import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import {
  FARM_KIND_NONPROFIT_ID,
  GIVING_MULTIPLIER_NONPROFIT_ID,
} from "@every.org/common/src/entity/constants";
import {
  DonationVisibility,
  FundraiserType,
  NonprofitType,
} from "@every.org/common/src/entity/types";

import { useLoggedInOrGuestUserOrUndefined } from "src/context/AuthContext/hooks";
import { AuthContextUser } from "src/context/AuthContext/types";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import { ApiStatus } from "src/utility/apiClient/types";

function getDefaultIsPublic(
  lastDonationVisibility: DonationVisibility | undefined,
  user: AuthContextUser | undefined,
  nonprofit: ContextNonprofit
) {
  if (nonprofit.metadata?.publicNotChecked) {
    return false;
  }
  if (!lastDonationVisibility) {
    return !user?.isPrivate;
  }
  switch (lastDonationVisibility) {
    case DonationVisibility.DEFAULT:
      return !user?.isPrivate;
    case DonationVisibility.EVERYONE:
      return true;
    case DonationVisibility.NO_ONE:
      return false;
  }
}

export const ShareInfo = ({
  form,
  formContext,
  nonprofit,
  fundraiser,
}: {
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  nonprofit: ContextNonprofit;
  fundraiser?: FundraiserResponse | null;
}) => {
  const { control, watch, setValue } = form;
  const { partnerWebhookDetailsResponse, requireShareInfo } = formContext;
  const toNonprofits = watch("toNonprofits");

  const loggedInOrGuestUser = useLoggedInOrGuestUserOrUndefined();

  const isPublic = watch("isPublic");
  const commentText = watch("commentText");
  const includeCommentText = watch("includeCommentText");

  const publicRequired = Boolean(includeCommentText && commentText?.length);

  const lastDonationVisibility = loggedInOrGuestUser?.lastDonationVisibility;
  useEffect(() => {
    // If we have not already defined a value for isPublic
    if (publicRequired) {
      setValue("isPublic", true);
      return;
    }
    if (isPublic === undefined) {
      setValue(
        "isPublic",
        getDefaultIsPublic(
          lastDonationVisibility,
          loggedInOrGuestUser,
          nonprofit
        )
      );
    }
  }, [
    isPublic,
    setValue,
    lastDonationVisibility,
    loggedInOrGuestUser,
    publicRequired,
    nonprofit,
  ]);

  // base case
  let entityName = nonprofit.metadata?.shareInfoName || nonprofit.name;

  // nonprofit list case
  if (nonprofit.type === NonprofitType.LIST) {
    const numNonprofits =
      toNonprofits?.length || nonprofit.endorsedNonprofitIds?.length;
    entityName = `${nonprofit.name} and the ${numNonprofits} nonprofits ${
      requireShareInfo ? "you're" : "I am"
    } supporting`;
  }

  if (
    fundraiser?.type === FundraiserType.MULTIPLE_NONPROFITS ||
    nonprofit.id === GIVING_MULTIPLIER_NONPROFIT_ID ||
    nonprofit.id === FARM_KIND_NONPROFIT_ID
  ) {
    entityName = "the nonprofits";
  }

  const partnerName =
    partnerWebhookDetailsResponse &&
    partnerWebhookDetailsResponse.status === ApiStatus.SUCCESS
      ? partnerWebhookDetailsResponse.response.partnerName
      : null;
  const partnerShareInfo =
    !!partnerWebhookDetailsResponse &&
    partnerWebhookDetailsResponse.status === ApiStatus.SUCCESS &&
    partnerWebhookDetailsResponse.response?.metadata?.excludePartnerPII !==
      true;
  const fundraiserCreatorName = useFundraiserCreatorName(fundraiser);
  const { collectAddress, hasAddress } = useCollectAddress(formContext);
  const includeAddress = collectAddress !== COLLECT_ADDRESS.NO || !!hasAddress;
  useEffect(() => {
    if (fundraiserCreatorName) {
      setValue("shareInfoWithFundraiserCreator", true);
      return;
    }
  }, [fundraiserCreatorName, setValue]);
  return (
    <React.Fragment>
      {fundraiserCreatorName && (
        <Controller
          control={control}
          name="shareInfoWithFundraiserCreator"
          render={({ field: { onChange, value } }) => (
            <ShareInfoCheckbox
              dataTname="toggleShareInfoWithFundraiserCreator"
              checked={value}
              onClick={() => {
                onChange(!value);
              }}
              name="shareInfoWithFundraiserCreator"
              id="shareInfoWithFundraiserCreator"
              labelCss={css`
                color: var(
                  ${value
                    ? colorCssVars.text.body
                    : colorCssVars.text.secondary}
                );
                ${css`
                  cursor: pointer;
                `};
              `}
              labelText={getShareEmailText(
                loggedInOrGuestUser,
                fundraiserCreatorName
              )}
              infoText={getSharePopoverText(
                ShareCheckboxType.FUNDRAISER_CREATOR,
                fundraiserCreatorName,
                false,
                includeAddress
              )}
            />
          )}
        />
      )}
      <Controller
        control={control}
        name="shareInfo"
        render={({ field: { onChange, value } }) => (
          <ShareInfoCheckbox
            dataTname="toggleShareInfo"
            checked={value}
            onClick={() => {
              if (!requireShareInfo) {
                onChange(!value);
                if (
                  fundraiser?.metadata?.mailingListCheckboxText ||
                  nonprofit.metadata?.mailingListCheckboxText
                ) {
                  value && form.setValue("optInToMailingList", false);
                }
              }
            }}
            name="shareInfo"
            id="shareInfo"
            disabled={!!requireShareInfo}
            labelCss={css`
              color: var(
                ${value && !requireShareInfo
                  ? colorCssVars.text.body
                  : colorCssVars.text.secondary}
              );
              ${!requireShareInfo
                ? css`
                    cursor: pointer;
                  `
                : ""};
            `}
            labelText={
              nonprofit.metadata?.shareInfoText ||
              (requireShareInfo
                ? `Your email will be shared with ${entityName}`
                : getShareEmailText(loggedInOrGuestUser, entityName))
            }
            infoText={getSharePopoverText(
              ShareCheckboxType.EMAIL,
              entityName,
              !!requireShareInfo,
              includeAddress
            )}
          />
        )}
      />
      {partnerShareInfo && (
        <ShareInfoCheckbox
          // it is necessary to match the spacing
          checkboxCss={{ opacity: 0 }}
          labelCss={css`
            color: var(${colorCssVars.text.secondary});
          `}
          dataTname="toggleSharePartnerInfo"
          checked
          name="sharePartnerInfo"
          disabled
          labelText={`Your contact info will be shared with ${
            partnerName || "the partner who referred you to Every.org"
          }`}
        />
      )}
      <Controller
        control={control}
        name="isPublic"
        render={({ field: { onChange, value } }) => (
          <ShareInfoCheckbox
            dataTname="toggleIsPublic"
            checked={value}
            onClick={() => {
              onChange(!value);
            }}
            name="isPublic"
            id="isPublic"
            disabled={publicRequired}
            showCheckboxWhenDisabled
            labelCss={css`
              color: var(
                ${value ? colorCssVars.text.body : colorCssVars.text.secondary}
              );
              ${publicRequired
                ? ""
                : css`
                    cursor: pointer;
                  `};
            `}
            labelText="Share my support publicly"
            infoText={getSharePopoverText(
              ShareCheckboxType.IS_PUBLIC,
              nonprofit.name,
              publicRequired,
              includeAddress
            )}
          />
        )}
      />
      {(fundraiser?.metadata?.mailingListCheckboxText ||
        nonprofit.metadata?.mailingListCheckboxText) && (
        <Controller
          control={control}
          name="optInToMailingList"
          render={({ field: { onChange, value } }) => (
            <ShareInfoCheckbox
              dataTname="toggleOptInToMailingList"
              checked={value}
              onClick={() => {
                onChange(!value);
                !value && form.setValue("shareInfo", true);
              }}
              name="optInToMailingList"
              id="optInToMailingList"
              disabled={!!requireShareInfo}
              labelCss={css`
                color: var(
                  ${value && !requireShareInfo
                    ? colorCssVars.text.body
                    : colorCssVars.text.secondary}
                );
                ${!requireShareInfo
                  ? css`
                      cursor: pointer;
                    `
                  : ""};
              `}
              labelText={
                fundraiser?.metadata?.mailingListCheckboxText ||
                nonprofit.metadata?.mailingListCheckboxText ||
                ""
              }
            />
          )}
        />
      )}
    </React.Fragment>
  );
};
